import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Public helper routines (visit counter, short links, general-board rank
 * state) run through server functions with the privileged client so the
 * underlying SECURITY DEFINER database functions are NOT executable by
 * anonymous callers directly.
 */

/** Increments the all-time visit counter once per browser session. */
export const trackVisit = createServerFn({ method: "POST" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("increment_visit");
  if (error) return { visits: null };
  return { visits: typeof data === "number" ? data : null };
});

/** Resolves a short code ("/ab3k9x") to its full site path, counting the click. */
export const resolveShortLink = createServerFn({ method: "GET" })
  .inputValidator((data) =>
    z
      .object({ code: z.string().toLowerCase().regex(/^[a-z0-9]{4,8}$/) })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: target } = await supabaseAdmin.rpc("resolve_short_link", {
      _code: data.code,
    });
    return { path: typeof target === "string" ? target : null };
  });

/** Returns (creating on first use) the short code for a same-site path. */
export const getOrCreateShortLink = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z
      .object({
        path: z
          .string()
          .regex(/^\/[A-Za-z0-9/_\-.]{0,180}$/)
          .max(181),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: code } = await supabaseAdmin.rpc("get_or_create_short_link", {
      _path: data.path,
    });
    return { code: typeof code === "string" ? code : null };
  });

export type GeneralBoardRank = {
  listing_id: string;
  current_rank: number;
  previous_rank: number | null;
  is_new: boolean;
  heat_cents: number;
  moved_at: string | null;
  first_seen_at: string | null;
};

/** Current General-board rank state (movement tracking). Public read. */
export const getGeneralBoardRanks = createServerFn({ method: "GET" }).handler(
  async (): Promise<GeneralBoardRank[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin.rpc("general_board_ranks");
    if (error) throw new Error(error.message);
    return (data as GeneralBoardRank[] | null) ?? [];
  },
);
